import { inject, Injectable } from '@angular/core';
import type {
  CancelWishlistReservationByManagerRequest,
  CancelWishlistReservationResponse,
  ConfirmWishlistReservationRequest,
  CreateWishlistItemRequest,
  CreateWishlistRequest,
  CreateWishlistShareLinkRequest,
  CreateWishlistShareLinkResponse,
  DeleteWishlistItemResponse,
  DisableWishlistShareLinkResponse,
  GuestReservationConfirmedResponse,
  GuestReservationRequest,
  GuestReservationStartedResponse,
  InviteWishlistGuestRequest,
  InviteWishlistGuestResponse,
  RemoveWishlistGuestResponse,
  RotateWishlistShareLinkResponse,
  UpdateWishlistAccessPolicyRequest,
  UpdateWishlistAccessPolicyResponse,
  UpdateWishlistItemRequest,
  UpdateWishlistRequest,
  WishlistManagerDetail,
} from '@nexus/api-types';
import type {
  WishlistAccessPolicyUpdateViewModel,
  WishlistDetailViewModel,
  WishlistGuestRemovalViewModel,
  WishlistInvitedGuestViewModel,
  WishlistItemDeletionViewModel,
  WishlistReservationCancellationViewModel,
  WishlistReservationConfirmationViewModel,
  WishlistReservationStartViewModel,
  WishlistShareLinkSecretViewModel,
  WishlistShareLinkViewModel,
} from '@nexus/customer/wishlists/model';
import { catchError, map, of, shareReplay, startWith, type Observable } from 'rxjs';
import { mapWishlistRequestError } from '../errors/map-wishlist-request-error';
import {
  mapDisabledWishlistShareLinkResponseToViewModel,
  mapWishlistAccessPolicyUpdateResponseToViewModel,
  mapWishlistGuestRemovalResponseToViewModel,
  mapWishlistInvitedGuestToViewModel,
  mapWishlistItemDeletionResponseToViewModel,
  mapWishlistManagerDetailToViewModel,
  mapWishlistReservationCancellationResponseToViewModel,
  mapWishlistReservationConfirmationResponseToViewModel,
  mapWishlistReservationStartResponseToViewModel,
  mapWishlistShareLinkSecretResponseToViewModel,
} from '../mappers/wishlist-view-model.mapper';
import type { WishlistRequestState } from '../types/wishlist-request-state.type';
import { WishlistApiClientService } from './wishlist-api-client.service';

@Injectable({
  providedIn: 'root',
})
export class WishlistMutationsService {
  private readonly apiClient: WishlistApiClientService = inject(WishlistApiClientService);

  public createWishlist(request: CreateWishlistRequest): Observable<WishlistRequestState<WishlistDetailViewModel>> {
    return this.toRequestState(
      this.apiClient
        .createWishlist(request)
        .pipe(
          map(
            (response: WishlistManagerDetail): WishlistDetailViewModel =>
              mapWishlistManagerDetailToViewModel(response),
          ),
        ),
    );
  }

  public updateWishlist(
    wishlistId: string,
    request: UpdateWishlistRequest,
  ): Observable<WishlistRequestState<WishlistDetailViewModel>> {
    return this.toRequestState(
      this.apiClient
        .updateWishlist(wishlistId, request)
        .pipe(
          map(
            (response: WishlistManagerDetail): WishlistDetailViewModel =>
              mapWishlistManagerDetailToViewModel(response),
          ),
        ),
    );
  }

  public addWishlistItem(
    wishlistId: string,
    request: CreateWishlistItemRequest,
  ): Observable<WishlistRequestState<WishlistDetailViewModel>> {
    return this.toRequestState(
      this.apiClient
        .addWishlistItem(wishlistId, request)
        .pipe(
          map(
            (response: WishlistManagerDetail): WishlistDetailViewModel =>
              mapWishlistManagerDetailToViewModel(response),
          ),
        ),
    );
  }

  public updateWishlistItem(
    wishlistId: string,
    itemId: string,
    request: UpdateWishlistItemRequest,
  ): Observable<WishlistRequestState<WishlistDetailViewModel>> {
    return this.toRequestState(
      this.apiClient
        .updateWishlistItem(wishlistId, itemId, request)
        .pipe(
          map(
            (response: WishlistManagerDetail): WishlistDetailViewModel =>
              mapWishlistManagerDetailToViewModel(response),
          ),
        ),
    );
  }

  public deleteWishlistItem(
    wishlistId: string,
    itemId: string,
  ): Observable<WishlistRequestState<WishlistItemDeletionViewModel>> {
    return this.toRequestState(
      this.apiClient
        .deleteWishlistItem(wishlistId, itemId)
        .pipe(
          map(
            (response: DeleteWishlistItemResponse): WishlistItemDeletionViewModel =>
              mapWishlistItemDeletionResponseToViewModel(response),
          ),
        ),
    );
  }

  public archiveWishlist(wishlistId: string): Observable<WishlistRequestState<WishlistDetailViewModel>> {
    return this.toRequestState(
      this.apiClient
        .archiveWishlist(wishlistId)
        .pipe(
          map(
            (response: WishlistManagerDetail): WishlistDetailViewModel =>
              mapWishlistManagerDetailToViewModel(response),
          ),
        ),
    );
  }

  public updateAccessPolicy(
    wishlistId: string,
    request: UpdateWishlistAccessPolicyRequest,
  ): Observable<WishlistRequestState<WishlistAccessPolicyUpdateViewModel>> {
    return this.toRequestState(
      this.apiClient
        .updateAccessPolicy(wishlistId, request)
        .pipe(
          map(
            (response: UpdateWishlistAccessPolicyResponse): WishlistAccessPolicyUpdateViewModel =>
              mapWishlistAccessPolicyUpdateResponseToViewModel(response),
          ),
        ),
    );
  }

  public createShareLink(
    wishlistId: string,
    request: CreateWishlistShareLinkRequest,
  ): Observable<WishlistRequestState<WishlistShareLinkSecretViewModel>> {
    return this.toRequestState(
      this.apiClient
        .createShareLink(wishlistId, request)
        .pipe(
          map(
            (response: CreateWishlistShareLinkResponse): WishlistShareLinkSecretViewModel =>
              mapWishlistShareLinkSecretResponseToViewModel(response),
          ),
        ),
    );
  }

  public rotateShareLink(wishlistId: string): Observable<WishlistRequestState<WishlistShareLinkSecretViewModel>> {
    return this.toRequestState(
      this.apiClient
        .rotateShareLink(wishlistId)
        .pipe(
          map(
            (response: RotateWishlistShareLinkResponse): WishlistShareLinkSecretViewModel =>
              mapWishlistShareLinkSecretResponseToViewModel(response),
          ),
        ),
    );
  }

  public disableShareLink(wishlistId: string): Observable<WishlistRequestState<WishlistShareLinkViewModel>> {
    return this.toRequestState(
      this.apiClient
        .disableShareLink(wishlistId)
        .pipe(
          map(
            (response: DisableWishlistShareLinkResponse): WishlistShareLinkViewModel =>
              mapDisabledWishlistShareLinkResponseToViewModel(response),
          ),
        ),
    );
  }

  public inviteGuest(
    wishlistId: string,
    request: InviteWishlistGuestRequest,
  ): Observable<WishlistRequestState<WishlistInvitedGuestViewModel>> {
    return this.toRequestState(
      this.apiClient
        .inviteGuest(wishlistId, request)
        .pipe(
          map(
            (response: InviteWishlistGuestResponse): WishlistInvitedGuestViewModel =>
              mapWishlistInvitedGuestToViewModel(response.guest),
          ),
        ),
    );
  }

  public removeInvitedGuest(
    wishlistId: string,
    invitationId: string,
  ): Observable<WishlistRequestState<WishlistGuestRemovalViewModel>> {
    return this.toRequestState(
      this.apiClient
        .removeInvitedGuest(wishlistId, invitationId)
        .pipe(
          map(
            (response: RemoveWishlistGuestResponse): WishlistGuestRemovalViewModel =>
              mapWishlistGuestRemovalResponseToViewModel(response),
          ),
        ),
    );
  }

  public createReservation(
    request: GuestReservationRequest,
  ): Observable<WishlistRequestState<WishlistReservationStartViewModel>> {
    return this.toRequestState(
      this.apiClient
        .createReservation(request)
        .pipe(
          map(
            (response: GuestReservationStartedResponse): WishlistReservationStartViewModel =>
              mapWishlistReservationStartResponseToViewModel(response),
          ),
        ),
    );
  }

  public reservationStatus(request: GuestReservationRequest): Observable<WishlistRequestState<WishlistReservationStartViewModel>> {
    return this.toRequestState(this.apiClient.reservationStatus(request).pipe(map(mapWishlistReservationStartResponseToViewModel)));
  }

  public resendReservation(request: GuestReservationRequest): Observable<WishlistRequestState<WishlistReservationStartViewModel>> {
    return this.toRequestState(this.apiClient.resendReservation(request).pipe(map(mapWishlistReservationStartResponseToViewModel)));
  }

  public confirmReservation(
    request: ConfirmWishlistReservationRequest,
  ): Observable<WishlistRequestState<WishlistReservationConfirmationViewModel>> {
    return this.toRequestState(
      this.apiClient
        .confirmReservation(request)
        .pipe(
          map(
            (response: GuestReservationConfirmedResponse): WishlistReservationConfirmationViewModel =>
              mapWishlistReservationConfirmationResponseToViewModel(response),
          ),
        ),
    );
  }

  public cancelReservationByManager(
    wishlistId: string,
    reservationId: string,
    request: CancelWishlistReservationByManagerRequest,
  ): Observable<WishlistRequestState<WishlistReservationCancellationViewModel>> {
    return this.toRequestState(
      this.apiClient
        .cancelReservationByManager(wishlistId, reservationId, request)
        .pipe(
          map(
            (response: CancelWishlistReservationResponse): WishlistReservationCancellationViewModel =>
              mapWishlistReservationCancellationResponseToViewModel(response),
          ),
        ),
    );
  }

  private toRequestState<TData>(source: Observable<TData>): Observable<WishlistRequestState<TData>> {
    return source.pipe(
      map((data: TData): WishlistRequestState<TData> => createSuccessState(data)),
      startWith(createLoadingState<TData>()),
      catchError((error: unknown): Observable<WishlistRequestState<TData>> => of(createErrorState<TData>(error))),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }
}

function createLoadingState<TData>(): WishlistRequestState<TData> {
  return {
    status: 'loading',
    data: null,
    error: null,
  };
}

function createSuccessState<TData>(data: TData): WishlistRequestState<TData> {
  return {
    status: 'success',
    data,
    error: null,
  };
}

function createErrorState<TData>(error: unknown): WishlistRequestState<TData> {
  return {
    status: 'error',
    data: null,
    error: mapWishlistRequestError(error),
  };
}
